import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import "../Styles/FestivalRecords.css";

function FestivalRecords() {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [editFestival, setEditFestival] = useState({
    name: "",
    date: "",
    description: ""
  });

  // Selected image while editing
  const [editImage, setEditImage] = useState(null);

  // ========================================
  // LOAD FESTIVALS
  // ========================================

  const loadFestivals = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        "/api/festivals"
      );

      setFestivals(response.data);
    } catch (err) {
      console.error("Error loading festivals:", err);
      setError("Unable to load festival records.");
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // INITIAL LOAD
  // ========================================

  useEffect(() => {
    loadFestivals();
  }, []);

  // ========================================
  // EDIT FESTIVAL
  // ========================================

  const handleEdit = (festival) => {
    setEditingId(festival.id);

    setEditFestival({
      name: festival.name || "",
      date: festival.date || "",
      description: festival.description || ""
    });

    setEditImage(null);
  };

  // ========================================
  // EDIT FIELD CHANGE
  // ========================================

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditFestival((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // ========================================
  // IMAGE CHANGE
  // ========================================

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setEditImage(e.target.files[0]);
    } else {
      setEditImage(null);
    }
  };

  // ========================================
  // SAVE FESTIVAL
  // ========================================

  const handleSave = async (id) => {
    try {
      const formData = new FormData();

      formData.append("name", editFestival.name);
      formData.append("date", editFestival.date);
      formData.append("description", editFestival.description);

      if (editImage) {
        formData.append("file", editImage);
      }

      await axios.put(
        `/api/festivals/${id}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setEditingId(null);
      setEditImage(null);

      setEditFestival({
        name: "",
        date: "",
        description: ""
      });

      await loadFestivals();
    } catch (err) {
      console.error("Error updating festival:", err);
      alert("Unable to update festival record.");
    }
  };

  // ========================================
  // CANCEL EDIT
  // ========================================

  const handleCancel = () => {
    setEditingId(null);
    setEditImage(null);

    setEditFestival({
      name: "",
      date: "",
      description: ""
    });
  };

  // ========================================
  // DELETE FESTIVAL
  // ========================================

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this festival record?"
    );

    if (!confirmed) return;

    try {
      await axios.delete(`/api/festivals/${id}`);
      await loadFestivals();
    } catch (err) {
      console.error("Error deleting festival:", err);
      alert("Unable to delete festival record.");
    }
  };

  // ========================================
  // EXPORT TO EXCEL
  // ========================================

  const handleExportExcel = () => {
    if (festivals.length === 0) {
      alert("No festival records available to export.");
      return;
    }

    const excelData = festivals.map((festival, index) => ({
      "S.No": index + 1,
      "Festival Name": festival.name || "",
      "Date": festival.date || "",
      "Description": festival.description || "",
      "Status":
        festival.active === true
          ? "Active"
          : festival.active === false
          ? "Inactive"
          : ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Festival Records");

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 30 },
      { wch: 15 },
      { wch: 60 },
      { wch: 15 }
    ];

    XLSX.writeFile(workbook, "Festival_Records.xlsx");
  };

  // ========================================
  // RETURN
  // ========================================

  return (
    <div className="festival-records-page">
      {/* ================= HEADER ================= */}
      <div className="festival-records-header">
        <h2>🎉 Festival Records</h2>
        <div className="festival-header-buttons">
          <button className="festival-refresh-button" onClick={loadFestivals}>
            🔄 Refresh
          </button>
          <button className="festival-export-button" onClick={handleExportExcel}>
            📊 Export Excel
          </button>
        </div>
      </div>

      {/* ================= LOADING ================= */}
      {loading && (
        <p className="festival-records-message">Loading festival records...</p>
      )}

      {/* ================= ERROR ================= */}
      {error && <p className="festival-records-error">{error}</p>}

      {/* ================= NO RECORDS ================= */}
      {!loading && !error && festivals.length === 0 && (
        <p className="festival-records-message">No festival records available.</p>
      )}

      {/* ================= RECORDS ================= */}
      {!loading && !error && festivals.length > 0 && (
        <div className="festival-records-list">
          {festivals.map((festival) => (
            <div className="festival-record-card" key={festival.id}>
              
              {/* ================= DISPLAY RECORD VIEW ================= */}
              {editingId !== festival.id ? (
                <>
                  {/* Festival Image */}
                  {festival.imageName && (
                    <div className="festival-record-image">
                      <img
                        src={`/api/festivals/images/${festival.imageName}`}
                        alt={festival.name}
                      />
                    </div>
                  )}

                  <div className="festival-record-details">
                    <div className="festival-record-title-row">
                      <h3>{festival.name}</h3>
                      <div className="festival-record-actions">
                        <button
                          className="festival-edit-button"
                          onClick={() => handleEdit(festival)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="festival-delete-button"
                          onClick={() => handleDelete(festival.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    <div className="festival-record-row">
                      <span className="festival-label">Date: </span>
                      <span className="festival-value">{festival.date}</span>
                    </div>

                    <div className="festival-record-description">
                      <span className="festival-label">Description: </span>
                      <p>{festival.description || "No description"}</p>
                    </div>
                  </div>
                </>
              ) : (
                
                /* ================= EDIT FORM VIEW ================= */
                <div className="festival-edit-form">
                  <div className="festival-edit-row">
                    <label>Name</label>
                    <input
                      type="text"
                      name="name"
                      value={editFestival.name}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="festival-edit-row">
                    <label>Date</label>
                    <input
                      type="date"
                      name="date"
                      value={editFestival.date}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="festival-edit-row">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={editFestival.description}
                      onChange={handleEditChange}
                    />
                  </div>

                  {/* Current Image Preview */}
                  {festival.imageName && (
                    <div className="festival-edit-preview">
                      <label>Current Image:</label>
                      <div className="festival-record-image">
                        <img
                          src={`/api/festivals/images/${festival.imageName}`}
                          alt={festival.name}
                        />
                      </div>
                    </div>
                  )}

                  <div className="festival-edit-row">
                    <label>New Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>

                  <div className="festival-edit-buttons">
                    <button
                      className="festival-save-button"
                      onClick={() => handleSave(festival.id)}
                    >
                      💾 Save
                    </button>
                    <button className="festival-cancel-button" onClick={handleCancel}>
                      ✖ Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FestivalRecords;